import { A11yModule } from '@angular/cdk/a11y'
import { isPlatformBrowser } from '@angular/common'
import {
  AfterContentChecked,
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ContentChild,
  ElementRef,
  inject,
  Input,
  OnDestroy,
  PLATFORM_ID,
  QueryList,
  ViewChild,
  ViewChildren,
  ViewContainerRef,
} from '@angular/core'
import { animationFrameScheduler, debounceTime, fromEvent, map, share, takeUntil, tap, throttleTime } from 'rxjs'

import { AnalyticsService } from 'app/core/analytics/analytics.service'
import { AnalyticsEvent } from 'app/core/analytics/analytics-event.enum'
import { onDestroy } from 'app/utils/rxjs/on-destroy.observable'

import { IconButtonComponent } from '../icon-button/icon-button.component'
import { RippleDirective } from '../ripple/ripple.directive'
import { TranslatePipe } from '../translate.pipe'
import { CarouselItemDirective } from './carousel-item.directive'

export const enum AnalyticsCarouselEventAction {
  DOT_CLICK = 'DotClick',
  PREV_ARROW_CLICK = 'PrevArrowClick',
  NEXT_ARROW_CLICK = 'NextArrowClick',
}

@Component({
  selector: 'pai-carousel',
  templateUrl: './carousel.component.html',
  styleUrls: ['./carousel.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [A11yModule, IconButtonComponent, RippleDirective, TranslatePipe],
  host: {
    '(window:resize)': 'onResize()',
  },
})
export class CarouselComponent<T> implements AfterViewInit, AfterContentChecked, OnDestroy {
  /** auto scroll interval in seconds. 0 means off */
  @Input() interval: number

  @Input() data: readonly T[]

  @ContentChild(CarouselItemDirective, { static: true })
  protected itemTemplate: CarouselItemDirective<T>

  @ViewChild('containerRef', { static: true })
  protected containerRef: ElementRef

  @ViewChild('itemsContainerRef', { static: true, read: ViewContainerRef })
  protected itemsContainerRef: ViewContainerRef

  @ViewChildren('navDot')
  protected navDotRefs: QueryList<ElementRef>

  protected readonly isBrowser: boolean = isPlatformBrowser(inject(PLATFORM_ID))

  protected get count(): number {
    return this.data.length
  }

  protected get containerEl(): HTMLDivElement {
    return this.containerRef.nativeElement
  }

  private readonly onDestroy$ = onDestroy()

  /** 0-based index */
  private currentIndex: number

  private hasRendered: boolean

  private readonly analytics = inject(AnalyticsService)

  ngAfterContentChecked(): void {
    if (!this.hasRendered) {
      this.hasRendered = true
      this.renderItems()
    }
  }

  ngAfterViewInit() {
    if (this.isBrowser) {
      this.setupScrollListener()
    }
  }

  ngOnDestroy(): void {
    this.itemsContainerRef.clear()
  }

  protected goTo(index: number, userInteraction = true) {
    this.scrollTo(this.containerEl.offsetWidth * (index + 1))
    if (userInteraction) {
      this.analytics.trackEvent(AnalyticsEvent.CAROUSEL_INTERACTION, { action: AnalyticsCarouselEventAction.DOT_CLICK })
    }
  }

  protected prev(userInteraction = true) {
    this.scrollTo(this.containerEl.scrollLeft - this.containerEl.offsetWidth)
    if (userInteraction) {
      this.analytics.trackEvent(AnalyticsEvent.CAROUSEL_INTERACTION, {
        action: AnalyticsCarouselEventAction.PREV_ARROW_CLICK,
      })
    }
  }

  protected next(userInteraction = true) {
    this.scrollTo(this.containerEl.scrollLeft + this.containerEl.offsetWidth)
    if (userInteraction) {
      this.analytics.trackEvent(AnalyticsEvent.CAROUSEL_INTERACTION, {
        action: AnalyticsCarouselEventAction.NEXT_ARROW_CLICK,
      })
    }
  }

  protected onResize() {
    setTimeout(() => this.goTo(this.currentIndex, false), 100)
  }

  private renderItems() {
    // on server we only render ONE element to
    // 1st reduce the html-tags to be transferred (prevent elements which are not necessary for the FCP or would trigger a Layout Shift
    // 2nd prevent the browser from instantly loading the images from the not-yet-visible carousel items

    if (this.isBrowser) {
      // create item before first
      if (this.count > 1) {
        this.appendItem(this.data[this.data.length - 1])
      }

      // create actual items
      this.data.forEach(this.appendItem)
      // create item after last
      if (this.count > 1) {
        this.appendItem(this.data[0])
      }
    } else {
      this.appendItem(this.data[0])
    }
  }

  private readonly setupScrollListener = () => {
    const onScroll$ = fromEvent(this.containerEl, 'scroll').pipe(takeUntil(this.onDestroy$), share())

    onScroll$
      .pipe(
        throttleTime(0, animationFrameScheduler),
        map(this.calcProgress),
        map((v) => Math.round(v * 100) / 100),
        tap(this.enableInfiniteScrolling),
      )
      .subscribe(this.updatePaginationDots)

    const intervalMs = this.interval * 1000
    if (this.interval > 0) {
      onScroll$.pipe(debounceTime(intervalMs)).subscribe(() => this.next(false))
    }

    onScroll$.pipe(debounceTime(500)).subscribe(this.updateCurrentIndex)

    // at start scroll to the first item, which is at pos 1
    this.containerEl.scrollLeft = this.containerEl.offsetWidth
    this.updatePaginationDots(this.calcProgress())
  }

  private readonly calcProgress = (): number => {
    return this.containerEl.scrollLeft / this.containerEl.offsetWidth - 1
  }

  private readonly updatePaginationDots = (progress: number): void => {
    this.navDotRefs.forEach((el, ix, arr) => {
      let xProgress = progress
      if (el === this.navDotRefs.first && progress > arr.length - 1) {
        xProgress = arr.length - progress
      }
      if (el === this.navDotRefs.last && progress < 0) {
        xProgress = arr.length + progress
      }
      const itemProgress = Math.max(0, 1 - Math.abs(ix - xProgress))
      ;(<HTMLElement>el.nativeElement).style.setProperty('--progress', `${itemProgress}`)
    })
  }

  private readonly enableInfiniteScrolling = (progress: number): void => {
    // bim letschte eis witer --> zrugg zum start
    if (progress === this.count) {
      setTimeout(() => {
        this.containerEl.scrollTo({ left: this.containerEl.offsetWidth, behavior: 'auto' })
      }, 100)
      // this.containerEl.scrollLeft =
    }
    // bim erschte eis zrugg --> zum ändi
    if (progress === -1) {
      setTimeout(() => {
        this.containerEl.scrollTo({ left: this.containerEl.offsetWidth * this.count, behavior: 'auto' })
      }, 100)
      // this.containerEl.scrollLeft = this.containerEl.offsetWidth * this.count
    }
  }

  private readonly scrollTo = (left: number): void => {
    this.containerEl.scroll({ behavior: 'smooth', left })
  }

  private readonly appendItem = (item: T): void => {
    this.itemsContainerRef.createEmbeddedView(this.itemTemplate.templateRef, { $implicit: item })
  }

  private readonly updateCurrentIndex = (): void => {
    // 1-based index
    const ix = (Math.round(this.containerEl.scrollLeft / this.containerEl.offsetWidth) - 1 + this.count) % this.count
    this.currentIndex = ix
  }
}
